import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import api from "../lib/api";
import LeadForm from "../components/LeadForm";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setService(null);
    setNotFound(false);
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#0B132B]" data-testid="service-not-found">
        <div className="text-center">
          <h1 className="font-display text-4xl font-bold text-white mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#F97316] text-sm font-semibold">← Back to all services</Link>
        </div>
      </div>
    );
  }

  if (!service) return <div className="min-h-[60vh] bg-[#0B132B]" data-testid="service-loading" />;

  return (
    <div className="bg-[#0B132B]" data-testid="service-detail-page">
      <section className="py-14 lg:py-20 bg-[#0f1830] border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/services" data-testid="back-to-services"
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#F97316] mb-6 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> All services
          </Link>
          {service.category && (
            <p className="font-mono2 text-xs uppercase tracking-widest text-[#F97316] mb-3" data-testid="service-category">
              {service.category.name}
            </p>
          )}
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight leading-none" data-testid="service-title">
            {service.title}
          </h1>
          {service.turnaround && (
            <div className="inline-flex items-center gap-2 mt-5 border border-[#1E293B] bg-[#131C38] rounded-full px-4 py-1.5">
              <Clock className="w-3.5 h-3.5 text-[#F97316]" />
              <span className="font-mono2 text-xs text-slate-300" data-testid="service-turnaround">{service.turnaround}</span>
            </div>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl font-bold text-white mb-4">About This Service</h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-10" data-testid="service-description">
            {service.description}
          </p>
          {service.features?.length > 0 && (
            <>
              <h2 className="font-display text-2xl font-bold text-white mb-5">What's Included</h2>
              <ul className="space-y-3 mb-10" data-testid="service-features">
                {service.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle2 className="w-4.5 h-4.5 w-5 h-5 text-[#F97316] shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </>
          )}
          {service.related?.length > 0 && (
            <>
              <h2 className="font-display text-2xl font-bold text-white mb-5">Related Services</h2>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="related-services">
                {service.related.map((r) => (
                  <Link key={r.id} to={`/services/${r.slug}`} data-testid={`related-service-${r.slug}`}
                    className="group bg-[#131C38] border border-[#1E293B] rounded-lg p-4 hover:border-[#F97316]/60 transition-colors flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-200 group-hover:text-[#F97316] transition-colors">{r.title}</span>
                    <ArrowRight className="w-4 h-4 text-[#F97316] shrink-0" />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
        <aside>
          <div className="bg-[#131C38] border border-[#1E293B] rounded-lg p-7 sticky top-24">
            <LeadForm serviceSlug={service.slug} title={`Get Help With ${service.title}`} />
          </div>
        </aside>
      </div>
    </div>
  );
}
